/**
 * Audio Recording Service for Vocalira
 * Allows users to record and replay their own voice
 * OPTIMIZED for cross-browser compatibility and quality
 */

class AudioRecorderService {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.lastRecording = null;
    this.isRecording = false;
    this.stream = null;
    this.recordingStartTime = null;
    
    // Device detection for optimal settings
    this.deviceInfo = this.detectDevice();
  }
  
  /**
   * Detect device/browser for optimized settings
   */
  detectDevice() {
    const ua = navigator.userAgent;
    return {
      isIOS: /iPad|iPhone|iPod/.test(ua),
      isAndroid: /Android/.test(ua),
      isSafari: /^((?!chrome|android).)*safari/i.test(ua),
      isChrome: /Chrome/.test(ua),
      isFirefox: /Firefox/.test(ua),
    };
  }

  /**
   * Check if audio recording is supported
   */
  isSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
  }

  /**
   * Request microphone access and initialize recorder
   */
  async initialize() {
    if (!this.isSupported()) {
      throw new Error('Gravação de áudio não suportada neste navegador');
    }

    try {
      // Build constraints based on device
      const audioConstraints = this.buildConstraints();
      
      console.log('[AudioRecorder] Requesting mic with constraints:', audioConstraints);
      
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: audioConstraints
      });
      
      // Log actual settings
      const track = this.stream.getAudioTracks()[0];
      if (track) {
        console.log('[AudioRecorder] Track settings:', track.getSettings());
      }
      
      return true;
    } catch (error) {
      console.error('[AudioRecorder] Initialize error:', error);
      
      if (error.name === 'NotAllowedError') {
        throw new Error('Permissão de microfone negada');
      } else if (error.name === 'NotFoundError') {
        throw new Error('Nenhum microfone encontrado');
      } else if (error.name === 'OverconstrainedError') {
        // Retry with basic constraints
        console.warn('[AudioRecorder] Retrying with basic constraints');
        return this.initializeBasic();
      }
      throw new Error('Erro ao acessar microfone');
    }
  }
  
  /**
   * Build audio constraints based on device
   */
  buildConstraints() {
    const constraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    
    // Add sample rate for non-iOS devices
    if (!this.deviceInfo.isIOS) {
      constraints.sampleRate = { ideal: 48000 };
      constraints.channelCount = 1;
    }
    
    return constraints;
  }
  
  /**
   * Fallback initialization with minimal constraints
   */
  async initializeBasic() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true 
      });
      return true;
    } catch (error) {
      throw new Error('Erro ao acessar microfone');
    }
  }

  /**
   * Start recording audio
   */
  async startRecording(onDataAvailable) {
    if (!this.stream) {
      await this.initialize();
    }

    this.audioChunks = [];
    this.recordingStartTime = Date.now();
    
    // Determine best mime type
    const mimeType = this.getBestMimeType();
    
    console.log('[AudioRecorder] Starting recording with mimeType:', mimeType);
    
    try {
      this.mediaRecorder = new MediaRecorder(this.stream, { 
        mimeType,
        audioBitsPerSecond: 128000 // Good quality for voice
      });
    } catch (e) {
      // Fallback without options
      console.warn('[AudioRecorder] Falling back to default MediaRecorder');
      this.mediaRecorder = new MediaRecorder(this.stream);
    }
    
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
        if (onDataAvailable) onDataAvailable(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType });
      const duration = Date.now() - this.recordingStartTime;
      
      this.lastRecording = {
        blob: audioBlob,
        url: URL.createObjectURL(audioBlob),
        timestamp: Date.now(),
        duration,
        mimeType: this.mediaRecorder.mimeType,
      };
      
      console.log('[AudioRecorder] Recording complete:', {
        duration: duration + 'ms',
        size: audioBlob.size + ' bytes',
        mimeType: this.mediaRecorder.mimeType,
      });
    };
    
    this.mediaRecorder.onerror = (event) => {
      console.error('[AudioRecorder] Recording error:', event.error);
    };

    // Collect data every 100ms for responsive feedback
    this.mediaRecorder.start(100);
    this.isRecording = true;
  }
  
  /**
   * Get best supported mime type
   */
  getBestMimeType() {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg',
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    
    return 'audio/webm'; // Fallback
  }

  /**
   * Stop recording
   * @returns {Object} Recording data with blob and URL
   */
  stopRecording() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        resolve(this.lastRecording);
        return;
      }

      this.mediaRecorder.onstop = () => {
        const mimeType = this.mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        const duration = Date.now() - this.recordingStartTime;
        
        this.lastRecording = {
          blob: audioBlob,
          url: URL.createObjectURL(audioBlob),
          timestamp: Date.now(),
          duration,
          mimeType,
        };
        
        this.isRecording = false;
        
        console.log('[AudioRecorder] Recording stopped:', {
          duration: duration + 'ms',
          size: audioBlob.size + ' bytes',
        });
        
        resolve(this.lastRecording);
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Get the last recording
   */
  getLastRecording() {
    return this.lastRecording;
  }

  /**
   * Play the last recording
   * @returns {HTMLAudioElement} Audio element for playback control
   */
  playLastRecording() {
    if (!this.lastRecording?.url) {
      console.warn('[AudioRecorder] No recording available');
      return null;
    }

    const audio = new Audio(this.lastRecording.url);
    audio.play();
    return audio;
  }

  /**
   * Clear the last recording
   */
  clearRecording() {
    if (this.lastRecording?.url) {
      URL.revokeObjectURL(this.lastRecording.url);
    }
    this.lastRecording = null;
    this.audioChunks = [];
  }

  /**
   * Release all resources
   */
  dispose() {
    console.log('[AudioRecorder] Disposing...');
    this.clearRecording();
    
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        // Ignore
      }
    }
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
        console.log('[AudioRecorder] Stopped track:', track.kind);
      });
      this.stream = null;
    }
    
    this.mediaRecorder = null;
  }
}

// Singleton instance
const audioRecorder = new AudioRecorderService();

export default audioRecorder;
export { AudioRecorderService };
